import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlus, FiSearch, FiFilter, FiEye, FiEdit, FiTrash2, FiDownload,
  FiMail, FiCheck, FiX, FiClock, FiCreditCard, FiUser, FiCalendar,
  FiChevronDown, FiChevronUp, FiMoreVertical, FiRefreshCw, FiFileText
} from 'react-icons/fi';
import { format, isAfter, isBefore, addDays } from 'date-fns';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { Quote, Invoice } from '../../types';
import { ConvertQuoteModal } from '../../components/quotes/ConvertQuoteModal';
import { formatRandSimple, formatRandNoDecimals } from '../../utils/currency';
import styles from './QuoteList.module.css';

const mockQuotes: Quote[] = [
  {
    id: '1',
    quoteNumber: 'Q-2024-001',
    clientId: '1',
    client: { 
      id: '1', 
      name: 'TechCorp Solutions', 
      email: 'contact@techcorp.com',
      phone: '+1-555-0123',
      paymentTerms: 'net30',
      tags: ['technology', 'enterprise'],
      status: 'active',
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-15')
    },
    items: [
      { id: '1', description: 'Web Development Services', quantity: 1, unitPrice: 5000, taxRate: 10, taxAmount: 500, total: 5500 },
      { id: '2', description: 'UI/UX Design', quantity: 1, unitPrice: 2500, taxRate: 10, taxAmount: 250, total: 2750 }
    ],
    subtotal: 7500,
    taxAmount: 750,
    discountAmount: 0,
    total: 8250,
    status: 'sent',
    issueDate: new Date('2024-01-15'),
    validUntil: new Date('2024-02-15'),
    notes: 'Premium web development package with modern design',
    createdBy: 'John Doe',
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15')
  },
  {
    id: '2',
    quoteNumber: 'Q-2024-002',
    clientId: '2',
    client: { 
      id: '2', 
      name: 'Global Enterprises', 
      email: 'procurement@global.com',
      phone: '+1-555-0456',
      paymentTerms: 'net45',
      tags: ['consulting', 'global'],
      status: 'active',
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-10')
    },
    items: [
      { id: '3', description: 'Consulting Services', quantity: 40, unitPrice: 150, taxRate: 10, taxAmount: 600, total: 6600 },
      { id: '4', description: 'Project Management', quantity: 1, unitPrice: 3000, taxRate: 10, taxAmount: 300, total: 3300 }
    ],
    subtotal: 9000,
    taxAmount: 900,
    discountAmount: 500,
    total: 9400,
    status: 'accepted',
    issueDate: new Date('2024-01-10'),
    validUntil: new Date('2024-02-10'),
    acceptedDate: new Date('2024-01-20'),
    notes: 'Strategic consulting and project management services',
    createdBy: 'Jane Smith',
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-20')
  },
  {
    id: '3',
    quoteNumber: 'Q-2024-003',
    clientId: '3',
    client: { 
      id: '3', 
      name: 'Startup Innovations', 
      email: 'hello@startup.com',
      phone: '+1-555-0789',
      paymentTerms: 'net30',
      tags: ['startup', 'mobile'],
      status: 'active',
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-20')
    },
    items: [
      { id: '5', description: 'Mobile App Development', quantity: 1, unitPrice: 8000, taxRate: 10, taxAmount: 800, total: 8800 },
      { id: '6', description: 'Backend API Development', quantity: 1, unitPrice: 4000, taxRate: 10, taxAmount: 400, total: 4400 }
    ],
    subtotal: 12000,
    taxAmount: 1200,
    discountAmount: 0,
    total: 13200,
    status: 'draft',
    issueDate: new Date('2024-01-20'),
    validUntil: new Date('2024-02-20'),
    notes: 'Complete mobile app solution with backend services',
    createdBy: 'Mike Johnson',
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-01-20')
  },
  {
    id: '4',
    quoteNumber: 'Q-2024-004',
    clientId: '4',
    client: { 
      id: '4', 
      name: 'Retail Solutions Ltd', 
      email: 'info@retail.com',
      phone: '+1-555-0321',
      paymentTerms: 'net60',
      tags: ['retail', 'ecommerce'],
      status: 'active',
      createdAt: new Date('2023-12-01'),
      updatedAt: new Date('2023-12-01')
    },
    items: [
      { id: '7', description: 'E-commerce Platform', quantity: 1, unitPrice: 15000, taxRate: 10, taxAmount: 1500, total: 16500 },
      { id: '8', description: 'Payment Integration', quantity: 1, unitPrice: 2000, taxRate: 10, taxAmount: 200, total: 2200 }
    ],
    subtotal: 17000,
    taxAmount: 1700,
    discountAmount: 1000,
    total: 18700,
    status: 'expired',
    issueDate: new Date('2023-12-01'),
    validUntil: new Date('2024-01-01'),
    notes: 'Complete e-commerce solution with payment processing',
    createdBy: 'Sarah Wilson',
    createdAt: new Date('2023-12-01'),
    updatedAt: new Date('2023-12-01')
  },
  {
    id: '5',
    quoteNumber: 'Q-2024-005',
    clientId: '5',
    client: { 
      id: '5', 
      name: 'Healthcare Systems', 
      email: 'contact@healthcare.com',
      phone: '+1-555-0654',
      paymentTerms: 'net30',
      tags: ['healthcare', 'security'],
      status: 'active',
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-05')
    },
    items: [
      { id: '9', description: 'Patient Management System', quantity: 1, unitPrice: 25000, taxRate: 10, taxAmount: 2500, total: 27500 },
      { id: '10', description: 'Data Security Implementation', quantity: 1, unitPrice: 5000, taxRate: 10, taxAmount: 500, total: 5500 }
    ],
    subtotal: 30000,
    taxAmount: 3000,
    discountAmount: 0,
    total: 33000,
    status: 'rejected',
    issueDate: new Date('2024-01-05'),
    validUntil: new Date('2024-02-05'),
    notes: 'Comprehensive healthcare management solution',
    createdBy: 'David Brown',
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-18')
  }
];

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' }
];

const clientOptions = [
  { value: 'all', label: 'All Clients' },
  { value: '1', label: 'TechCorp Solutions' },
  { value: '2', label: 'Global Enterprises' },
  { value: '3', label: 'Startup Innovations' },
  { value: '4', label: 'Retail Solutions Ltd' },
  { value: '5', label: 'Healthcare Systems' }
];

export function QuoteList() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Quote[]>(mockQuotes);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [selectedQuotes, setSelectedQuotes] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedQuoteForConversion, setSelectedQuoteForConversion] = useState<Quote | null>(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeView, setActiveView] = useState<'all' | 'sent' | 'draft' | 'accepted'>('all');

  const filteredQuotes = useMemo(() => {
    return quotes.filter(quote => {
      const matchesSearch = quote.quoteNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           quote.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           quote.notes?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Apply active view filter
      let matchesView = true;
      if (activeView === 'sent') {
        matchesView = quote.status === 'sent';
      } else if (activeView === 'draft') {
        matchesView = quote.status === 'draft';
      } else if (activeView === 'accepted') {
        matchesView = quote.status === 'accepted';
      }
      
      const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
      const matchesClient = clientFilter === 'all' || quote.client.id === clientFilter;
      
      return matchesSearch && matchesView && matchesStatus && matchesClient;
    }).sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'date':
          comparison = new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime();
          break;
        case 'amount':
          comparison = b.total - a.total;
          break;
        case 'client':
          comparison = a.client.name.localeCompare(b.client.name);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return sortOrder === 'asc' ? -comparison : comparison;
    });
  }, [quotes, searchTerm, statusFilter, clientFilter, sortBy, sortOrder, activeView]);

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      setQuotes(quotes.filter(quote => quote.id !== id));
      setSelectedQuotes(selectedQuotes.filter(quoteId => quoteId !== id));
      toast.success('Quote deleted successfully');
    }
  };

  const handleBulkDelete = () => {
    if (selectedQuotes.length === 0) {
      toast.warning('Please select quotes to delete');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${selectedQuotes.length} quotes?`)) {
      setQuotes(quotes.filter(quote => !selectedQuotes.includes(quote.id)));
      setSelectedQuotes([]);
      toast.success(`${selectedQuotes.length} quotes deleted successfully`);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedQuotes(filteredQuotes.map(quote => quote.id));
    } else {
      setSelectedQuotes([]);
    }
  };

  const handleSelectQuote = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedQuotes([...selectedQuotes, id]);
    } else {
      setSelectedQuotes(selectedQuotes.filter(quoteId => quoteId !== id));
    }
  };

  const handleConvertToInvoice = (quoteId: string) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (quote) {
      setSelectedQuoteForConversion(quote);
      setShowConvertModal(true);
    }
  };

  const handleConvertQuote = async (invoice: Invoice) => {
    try {
      // In a real app, this would save the invoice to the database
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Navigate to the new invoice
      navigate(`/invoices/${invoice.id}`);
      toast.success('Quote converted to invoice successfully!');
    } catch (error) {
      toast.error('Failed to convert quote to invoice');
    }
  };

  const handleSendQuote = (quoteId: string) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (quote) {
      if (quote.status === 'draft') {
        // Update status to sent
        setQuotes(quotes.map(q => 
          q.id === quoteId ? { ...q, status: 'sent' as const } : q
        ));
        toast.success('Quote sent successfully!');
      } else if (quote.status === 'sent') {
        // Resend existing quote
        toast.success('Quote resent successfully!');
      }
    }
    // In a real app, this would send the quote via email
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'var(--gray-500)', bg: 'var(--gray-100)', icon: <FiClock size={14} /> },
      sent: { color: 'var(--blue-600)', bg: 'var(--blue-100)', icon: <FiMail size={14} /> },
      accepted: { color: 'var(--green-600)', bg: 'var(--green-100)', icon: <FiCheck size={14} /> },
      rejected: { color: 'var(--red-600)', bg: 'var(--red-100)', icon: <FiX size={14} /> },
      expired: { color: 'var(--orange-600)', bg: 'var(--orange-100)', icon: <FiClock size={14} /> }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    return (
      <span className={styles.statusBadge} style={{ color: config.color, backgroundColor: config.bg }}>
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const isExpired = (validUntil: Date) => {
    return isBefore(validUntil, new Date());
  };

  const getDaysUntilExpiry = (validUntil: Date) => {
    const today = new Date();
    const diffTime = validUntil.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <motion.div 
      className={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Quotes</h1>
          <p className={styles.subtitle}>Manage and track your business quotes</p>
        </div>
        <div className={styles.headerRight}>
          <Link to="/quotes/create" className={styles.createButton}>
            <FiPlus size={16} />
            Create Quote
          </Link>
        </div>
      </div>

      {/* View Tabs */}
      <div className={styles.viewTabs}>
        <button
          className={`${styles.viewTab} ${activeView === 'all' ? styles.active : ''}`}
          onClick={() => setActiveView('all')}
        >
          <FiFileText size={16} />
          All Quotes
          <span className={styles.tabCount}>{quotes.length}</span>
        </button>
        <button
          className={`${styles.viewTab} ${activeView === 'sent' ? styles.active : ''}`}
          onClick={() => setActiveView('sent')}
        >
          <FiMail size={16} />
          Sent Quotes
          <span className={styles.tabCount}>{quotes.filter(q => q.status === 'sent').length}</span>
        </button>
        <button
          className={`${styles.viewTab} ${activeView === 'draft' ? styles.active : ''}`}
          onClick={() => setActiveView('draft')}
        >
          <FiClock size={16} />
          Drafts
          <span className={styles.tabCount}>{quotes.filter(q => q.status === 'draft').length}</span>
        </button>
        <button
          className={`${styles.viewTab} ${activeView === 'accepted' ? styles.active : ''}`}
          onClick={() => setActiveView('accepted')}
        >
          <FiCheck size={16} />
          Accepted
          <span className={styles.tabCount}>{quotes.filter(q => q.status === 'accepted').length}</span>
        </button>
      </div>

      {/* Sent Quotes Summary */}
      {activeView === 'sent' && (
        <motion.div 
          className={styles.sentQuotesSummary}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.summaryCard}>
            <div className={styles.summaryIcon}>
              <FiMail size={24} />
            </div>
            <div className={styles.summaryContent}>
              <h3>Sent Quotes Overview</h3>
              <p>You have {quotes.filter(q => q.status === 'sent').length} quotes currently sent to clients</p>
              <div className={styles.summaryStats}>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>Total Sent</span>
                  <span className={styles.statValue}>{quotes.filter(q => q.status === 'sent').length}</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>Total Value</span>
                  <span className={styles.statValue}>
                    {formatRandNoDecimals(quotes.filter(q => q.status === 'sent').reduce((sum, q) => sum + q.total, 0))}
                  </span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>Pending Response</span>
                  <span className={styles.statValue}>
                    {quotes.filter(q => q.status === 'sent' && !isExpired(q.validUntil)).length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Filters and Search */}
      <div className={styles.filtersSection}>
        <div className={styles.searchAndFilters}>
          <div className={styles.searchBox}>
            <FiSearch size={18} />
            <input
              type="text"
              placeholder="Search quotes by number, client, or notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          
          <button 
            className={styles.filterToggle}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter size={16} />
            Filters
            {showFilters ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
          </button>
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div 
              className={styles.filtersPanel}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className={styles.filterRow}>
                <div className={styles.filterGroup}>
                  <label>Status</label>
                  <Select
                    value={statusOptions.find(option => option.value === statusFilter)}
                    onChange={(option) => setStatusFilter(option?.value || 'all')}
                    options={statusOptions}
                    className={styles.select}
                    placeholder="Select status"
                  />
                </div>
                
                <div className={styles.filterGroup}>
                  <label>Client</label>
                  <Select
                    value={clientOptions.find(option => option.value === clientFilter)}
                    onChange={(option) => setClientFilter(option?.value || 'all')}
                    options={clientOptions}
                    className={styles.select}
                    placeholder="Select client"
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label>Sort By</label>
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className={styles.selectInput}
                  >
                    <option value="date">Date</option>
                    <option value="amount">Amount</option>
                    <option value="client">Client</option>
                    <option value="status">Status</option>
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label>Order</label>
                  <select 
                    value={sortOrder} 
                    onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                    className={styles.selectInput}
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bulk Actions */}
      {selectedQuotes.length > 0 && (
        <motion.div 
          className={styles.bulkActions}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <span className={styles.selectedCount}>
            {selectedQuotes.length} quote{selectedQuotes.length !== 1 ? 's' : ''} selected
          </span>
          <div className={styles.bulkButtons}>
            <button onClick={handleBulkDelete} className={styles.bulkDeleteButton}>
              <FiTrash2 size={14} />
              Delete Selected
            </button>
            <button className={styles.bulkSendButton}>
              <FiMail size={14} />
              Send Selected
            </button>
          </div>
        </motion.div>
      )}

      {/* Quotes Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  checked={selectedQuotes.length === filteredQuotes.length && filteredQuotes.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className={styles.checkbox}
                />
              </th>
              <th>Quote Number</th>
              <th>Client</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Issue Date</th>
              <th>Valid Until</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <AnimatePresence>
              {filteredQuotes.map((quote, index) => (
                <motion.tr
                  key={quote.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                  className={styles.tableRow}
                >
                  <td className={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      checked={selectedQuotes.includes(quote.id)}
                      onChange={(e) => handleSelectQuote(quote.id, e.target.checked)}
                      className={styles.checkbox}
                    />
                  </td>
                  <td>
                    <div className={styles.quoteNumber}>
                      <span className={styles.number}>{quote.quoteNumber}</span>
                      {quote.notes && (
                        <span className={styles.notes}>{quote.notes}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={styles.clientInfo}>
                      <span className={styles.clientName}>{quote.client.name}</span>
                      <span className={styles.clientEmail}>{quote.client.email}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.amount}>
                      <span className={styles.total}>{formatRandNoDecimals(quote.total)}</span>
                      <span className={styles.items}>{quote.items.length} item{quote.items.length !== 1 ? 's' : ''}</span>
                    </div>
                  </td>
                  <td>
                    {getStatusBadge(quote.status)}
                    {quote.status === 'sent' && isExpired(quote.validUntil) && (
                      <span className={styles.expiredBadge}>Expired</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.dateInfo}>
                      <span className={styles.date}>{format(quote.issueDate, 'MMM dd, yyyy')}</span>
                      <span className={styles.createdBy}>by {quote.createdBy}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.validUntil}>
                      <span className={styles.date}>{format(quote.validUntil, 'MMM dd, yyyy')}</span>
                      {quote.status === 'sent' && !isExpired(quote.validUntil) && (
                        <span className={styles.daysLeft}>
                          {getDaysUntilExpiry(quote.validUntil)} days left
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <Link to={`/quotes/${quote.id}`} className={styles.actionButton} title="View">
                        <FiEye size={16} />
                      </Link>
                      <Link to={`/quotes/${quote.id}/edit`} className={styles.actionButton} title="Edit">
                        <FiEdit size={16} />
                      </Link>
                      {quote.status === 'accepted' && (
                        <button 
                          onClick={() => handleConvertToInvoice(quote.id)}
                          className={styles.actionButton}
                          title="Convert to Invoice"
                        >
                          <FiCreditCard size={16} />
                        </button>
                      )}
                      {quote.status === 'draft' && (
                        <button 
                          onClick={() => handleSendQuote(quote.id)}
                          className={styles.actionButton}
                          title="Send Quote"
                        >
                          <FiMail size={16} />
                        </button>
                      )}
                      {quote.status === 'sent' && (
                        <button 
                          onClick={() => handleSendQuote(quote.id)}
                          className={styles.actionButton}
                          title="Resend Quote"
                        >
                          <FiMail size={16} />
                        </button>
                      )}
                      <button 
                        onClick={() => handleDelete(quote.id)}
                        className={`${styles.actionButton} ${styles.deleteButton}`}
                        title="Delete"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
        </table>

        {filteredQuotes.length === 0 && (
          <motion.div 
            className={styles.emptyState}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <FiFileText size={48} />
            <h3>No quotes found</h3>
            <p>Try adjusting your search or filters to find what you're looking for.</p>
            <Link to="/quotes/create" className={styles.createButton}>
              <FiPlus size={16} />
              Create Your First Quote
            </Link>
          </motion.div>
        )}
      </div>

      {/* Summary Stats */}
      <div className={styles.summaryStats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Total Quotes</span>
          <span className={styles.statValue}>{filteredQuotes.length}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Total Value</span>
          <span className={styles.statValue}>
            {formatRandNoDecimals(filteredQuotes.reduce((sum, quote) => sum + quote.total, 0))}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Accepted</span>
          <span className={styles.statValue}>
            {filteredQuotes.filter(q => q.status === 'accepted').length}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Pending</span>
          <span className={styles.statValue}>
            {filteredQuotes.filter(q => q.status === 'sent').length}
          </span>
        </div>
      </div>

      {/* Convert Quote Modal */}
      {selectedQuoteForConversion && (
        <ConvertQuoteModal
          quote={selectedQuoteForConversion}
          isOpen={showConvertModal}
          onClose={() => {
            setShowConvertModal(false);
            setSelectedQuoteForConversion(null);
          }}
          onConvert={handleConvertQuote}
        />
      )}
    </motion.div>
  );
}
